import React, { useContext, useState } from "react";
import {
  Box,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { assetIds, ThemedAsset } from "nemuru-components";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import {
  ICON_TITLE_HEIGHT,
  LENDER_APPROVAL_STATUS,
} from "../../../../../../config/config";
import Text from "../../../../../shared/components/Text";
import IconTitle from "../../../../../shared/components/IconTitle";
import { useLenderSelection } from "../hooks/useLenderSelection";
import { LenderSelectionItem } from "./LenderSelectionItem";
import { FullScreenLoading } from "../_shared/components/FullScreenLoading";
import { PendingApprovalMessage } from "./PendingApprovalMessage";
import { useLoanCalculatorsByPricings } from "../hooks/useLoanCalculatorsByPricings";
import MainButton from "../../../../../shared/components/MainButton";

export const LenderSelection = () => {
  const {
    state: { loanApplications },
  } = useContext(ProcessContext);
  const { palette, breakpoints } = useTheme();
  const isScreenXs = useMediaQuery(breakpoints.down("xs"));

  const { handleSubmit, areAllLoanApplicationsDenied } = useLenderSelection();
  const { loanCalculators, isLoadingPricings } = useLoanCalculatorsByPricings();

  const [selectedLoanApplicationId, setSelectedLoanApplicationId] =
    useState(undefined);

  const handleChange = (event) => {
    setSelectedLoanApplicationId(event.target.value);
  };

  const filteredLoanApplications = loanApplications.filter(
    (loanApplication) =>
      loanApplication.lender_fields.loan_application_status
        .lender_approval_status === LENDER_APPROVAL_STATUS.YES ||
      loanApplication.lender_fields.loan_application_status
        .lender_approval_status === LENDER_APPROVAL_STATUS.MAYBE
  );

  if (areAllLoanApplicationsDenied || isLoadingPricings) {
    return <FullScreenLoading />;
  }

  if (
    loanApplications.every(
      (loanApplication) =>
        loanApplication.lender_fields.lender_request_id === null
    ) ||
    filteredLoanApplications.length === 0
  ) {
    return <PendingApprovalMessage />;
  }

  const currentSelectedLoanApplication = filteredLoanApplications.find(
    (loanApplication) => loanApplication.id === selectedLoanApplicationId
  );

  const sortedLoanApplications = filteredLoanApplications
    .map((loanApplication) => {
      return {
        ...loanApplication,
        loanCalculator: loanCalculators.find(
          (loanCalculator) =>
            loanCalculator.lenderName === loanApplication.lender_name
        ),
      };
    })
    .filter((loanApplication) => loanApplication.loanCalculator)
    .sort((a, b) => a.loanCalculator.apr.value - b.loanCalculator.apr.value);

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_BANK}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId={`SELECT_LENDER`} />}
          subtitle={<Text tId={`SELECT_LENDER_SUBTITLE`} />}
        />
        <Box
          display={"flex"}
          flexDirection={"column"}
          gridGap={12}
          pt={isScreenXs ? 4 : 2}
        >
          <Box pb={1}>
            <Typography variant="subtitle2" color="textSecondary">
              <Text tId={"SELECT_ONE_OF_THE_FOLLOWING_OPTIONS"} />
            </Typography>
          </Box>
          <RadioGroup
            name={"selectedLoanApplicationId"}
            value={selectedLoanApplicationId}
            onChange={handleChange}
          >
            {sortedLoanApplications.map((loanApplication) => (
              <Box key={loanApplication.id} pb={1}>
                <LenderSelectionItem loanApplication={loanApplication} />
              </Box>
            ))}
          </RadioGroup>
        </Box>
      </Box>
      <Box pt={3}>
        <Typography variant="caption" color="textSecondary">
          <Text tId={"SELECT_LENDER_DESCRIPTION"} />
        </Typography>
        <MainButton
          disabled={!selectedLoanApplicationId}
          onClick={() => handleSubmit(currentSelectedLoanApplication)}
        >
          {selectedLoanApplicationId ? (
            <>
              <Text tId="CONTINUE_WITH" />{" "}
              <Text tId={currentSelectedLoanApplication.lender_name} />
            </>
          ) : (
            <Text tId="CONTINUE" />
          )}
        </MainButton>
      </Box>
    </>
  );
};
